import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import { Layout, Steps, Breadcrumb } from 'antd';
import classNames from 'classnames';
import chunk from 'lodash.chunk';
import { USER_ID } from '../../../store/modules/user/user.constants';
import breadcrumbSeparator from '../../../assets/icons/breadcrumbSeparator.svg';
import PageTitle from '../../lib/page-title';
import BreadcrumbItem from '../../lib/breadcrumbItem';
import { useActions } from '../../providers/actions-provider/actions.context';
import { useLocalStorage } from '../../providers/local-storage-provider/local-storage.context';
import { REPORTS_LIST_PATH } from '../../routing/paths.constants';
import FirstStepBase from './first-step-base.page';
import SecondStepTable from './second-step-table.page';
import ThirdStepFilters from './third-step-filters.page';
import { ICrumb } from './interfaces';
import { IColumn } from '../../../store/modules/report/i-column';
import './create-report.less';
import { useStore } from '../../../store';
import PermissionProvider from '../../providers/permissionProvider';

const CreateReport = (): JSX.Element => {
  const { listsForReport } = useStore();
  const { userActions, reportActions } = useActions();
  const { location: { pathname } } = useHistory();
  const localStorageService = useLocalStorage();
  const userID: number | null = localStorageService.get(USER_ID);
  const reportGroup_id = +pathname.slice(pathname.lastIndexOf('/') + 1);
  const { Step } = Steps;

  const [currentStep, setCurrentStep] = useState(0);
  const [fieldOrder, setFieldOrder] = useState(1);

  useEffect(() => {
    if (userID) {
      userActions.getCurrentUser(+userID);
    }
  }, [userID]);

  useEffect(() => {
    // TODO: we need to create independent data points for the filterFields
    // if (!listsForReport.loaded) {
    reportActions.getListsForReport(null);
    // }
  }, []);

  const formikForFirstStep = useFormik({
    initialValues: {
      name: '',
      description: '',
      code: '',
      collection: '',
      aggregate: '',
      reportGroup_id: reportGroup_id || null,
    },
    onSubmit: () => {
      setCurrentStep(1);
    },
  });

  const initialField: IColumn = {
    report_id: 0,
    order: fieldOrder,
    field: '',
    header: '',
    format: '',
    link: '',
    hidden: 0,
  };
  const formikForSecondStep = useFormik({
    initialValues: {
      fieldsTable: [initialField],
    },
    onSubmit: async () => null,
  });

  let dataForCrumbs: ICrumb[] = [];
  const initialCrumb = {
    path: REPORTS_LIST_PATH,
    breadcrumbName: 'Reports List',
  };
  if (pathname === REPORTS_LIST_PATH) {
    dataForCrumbs.push(initialCrumb);
  } else {
    // preparing routes for creation crumbs
    // without first three static '', 'reports-list', 'create-report'
    const prepareRoutes = pathname.split('/').slice(3);
    // break array by chunk of /name-of-nest and /id
    const chunks = chunk(prepareRoutes, 2);
    let path = REPORTS_LIST_PATH;
    const crumbsWithoutInitial = chunks.map((el) => {
      // each next path need to include previous
      path += `/${el[0]}/${el[1]}`;
      return {
        path,
        breadcrumbName: el[0],
      };
    });
    dataForCrumbs = [initialCrumb, ...crumbsWithoutInitial];
  }

  const filterFields = formikForSecondStep.values.fieldsTable.map(
    (ft) => ({ value: ft.field, text: ft.header }),
  );

  function switchStep(step: number) {
    let renderStep;
    switch (step) {
      case 0:
        renderStep = (
          <FirstStepBase
            formikForFirstStep={formikForFirstStep}
          />
        );
        break;
      case 1:
        renderStep = (
          <SecondStepTable
            collection={formikForFirstStep.values.collection}
            stepHandler={setCurrentStep}
            fieldOrder={fieldOrder}
            setFieldOrder={setFieldOrder}
            formikForSecondStep={formikForSecondStep}
            initialField={initialField}
          />
        );
        break;
      case 2:
        renderStep = (
          <ThirdStepFilters
            stepHandler={setCurrentStep}
            formikForFirstStep={formikForFirstStep}
            formikForSecondStep={formikForSecondStep}
            regex={listsForReport.data.regex}
            quickRanges={listsForReport.data.quickRanges}
            operators={listsForReport.data.operators}
            filterFields={filterFields}
          />
        );
        break;
      default:
        break;
    }
    return renderStep;
  }

  return (
    <Layout className="main-container">
      <PermissionProvider pageId="reports">
        <Breadcrumb
          itemRender={BreadcrumbItem}
          routes={dataForCrumbs}
          separator={<img src={breadcrumbSeparator} alt="breadcrumb separator" />}
          params={{ dataForCrumbs }}
        />
        <PageTitle title="New Report" back />
        <div className={classNames('filter__layout', { 'flex-off': currentStep === 1 })}>
          <Steps
            className={classNames('filter__steps', {
              'max-width-none': currentStep === 1,
            })}
            direction="vertical"
            size="small"
            current={currentStep}
          >
            <Step title="Base Information" />
            <Step title="Report Table" />
            <Step title="Filters" />
          </Steps>
          {switchStep(currentStep)}
        </div>
      </PermissionProvider>
    </Layout>
  );
};

CreateReport.displayName = 'CreateReport';
export default CreateReport;
